/*
 * Maps Box exclusion lists between Bender's stored strings and the editor's draft entries.
 * Pathname drafts hold user-facing globs, so conversion happens only here at the request
 * boundary; stored regexes that are not globs stay advanced and round-trip verbatim. Entries
 * are also described by match mode here, so display never has to know about stored regexes.
 */
import { globToRegExpSource, regExpSourceToGlob } from 'src/utils/globPattern';
import {
  fromPathnameGlob,
  type PathnameMatch,
  PathnameMatchModes,
  toPathnameGlob,
} from 'src/utils/pathnameMatchMode';

export interface ExclusionEntry {
  readonly value: string;
  /** A stored pathname regex that has no glob form; shown read-only and saved unchanged. */
  readonly isAdvanced: boolean;
}

export const toEditableEntries = (values: readonly string[]): ExclusionEntry[] =>
  values.map((value) => ({ value, isAdvanced: false }));

export const toPathnameEntries = (sources: readonly string[]): ExclusionEntry[] =>
  sources.map((source) => {
    const glob = regExpSourceToGlob(source);
    return glob === null ? { value: source, isAdvanced: true } : { value: glob, isAdvanced: false };
  });

/** Describes a pathname entry the way the user could have typed it; stored regexes read as advanced. */
export const toPathnameMatch = ({ value, isAdvanced }: ExclusionEntry): PathnameMatch =>
  isAdvanced ? { mode: PathnameMatchModes.Advanced, text: value } : fromPathnameGlob(value);

/** Builds the glob entry a plain-text match stores, the inverse of `toPathnameMatch` for glob entries. */
export const fromPathnameMatch = ({ mode, text }: PathnameMatch): ExclusionEntry => ({
  value: toPathnameGlob(mode, text),
  isAdvanced: false,
});

export const toExcludedPathnames = (entries: readonly ExclusionEntry[]): string[] =>
  entries.map(({ value, isAdvanced }) => (isAdvanced ? value : globToRegExpSource(value)));
